import {
  CardStatus,
  CardType
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  useModalContext,
  WSButton,
  WSButtons,
  WSSidebar,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { Card } from "../../../../ClientPayments/components/Card/Card";
import { Fullscreen } from "../../../../components/Fullscreen/Fullscreen";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import styles from "../../../../Invoices/screens/InvoicesInvoiceDetails.module.scss";
import {
  useLockCard,
  useTemrinateCard,
  useUnlockCard
} from "../../../../query/payments/mutations";
import { useCardQuery } from "../../../../query/payments/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { getParentPath } from "../../../../utils/goToParentRoute";
import { BankCard, CardSuccessPhysical } from "../BankCard/BankCard";
import { CHANGE_PIN_MODAL, ChangePin } from "../../modals/ChangePin";
import { SET_PIN_MODAL, SetPin } from "../../modals/SetPin";
import {
  BANK_CARD_OTP_MODAL,
  OTPModalInnerProps,
  useOTPProtector
} from "../BankCard/BankCardOTPModal";
import { useSetWSStore, useWSStore } from "../../../../store";
import { openInNewTab } from "../../../../utils/openInNewTab";
import {
  CONFIRM_TERMINATE_CARD_MODAL,
  ConfirmTerminateCard
} from "../../modals/ConfirmTerminate";
import {
  REPLACE_OR_TERMINATE_CARD_MODAL,
  ReplaceOrTerminateCard
} from "../../modals/ReplaceOrTerminate";
import {
  REPLACE_CARD_CONFIRMATION_MODAL,
  ReplaceCardConfirmation
} from "../../modals/ReplaceCardConfirmation";
import {
  LOST_OR_STOLEN_CONFIRMATION_MODAL,
  LostOrStolenConfirmation
} from "../../modals/LostOrStolenConfirmation";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { ACTIVATE_CARD_MODAL } from "./ActivateCard";

type Props = RouteComponentProps<{ cardId: string }, {}, { backPath: string }>;

export const BankCardDetails: React.FC<Props> = ({
  match,
  history,
  location
}) => {
  const backPath = location.state?.backPath || getParentPath(history);

  const onBack = () => {
    if (backPath) {
      history.push({
        pathname: backPath,
        search: location.search
      });
    }
  };

  return (
    <>
      <WSScreen.Mobile>
        <Fullscreen>
          <Inner cardId={match.params.cardId} onBack={onBack} />
        </Fullscreen>
      </WSScreen.Mobile>
      <WSScreen.TabletAndDesktop>
        <WSSidebar onClose={onBack} className={styles.drawer}>
          <Inner cardId={match.params.cardId} onBack={onBack} />
        </WSSidebar>
      </WSScreen.TabletAndDesktop>
    </>
  );
};

type InnerProps = {
  cardId: string;
  onBack: () => void;
};

const Inner: React.FC<InnerProps> = ({ cardId, onBack }) => {
  const cardQuery = useCardQuery(cardId);
  const history = useHistory();
  const { openModal } = useModalContext();
  const featureFlagsQuery = useFeatureFlags();
  const otpProtector = useOTPProtector();

  const [lockCard, lockCardMeta] = useLockCard();
  const [unlockCard, unlockCardMeta] = useUnlockCard();
  const [terminateCard, terminateCardMeta] = useTemrinateCard();

  const setStore = useSetWSStore();
  const {
    bankCardTokens: { token, ids }
  } = useWSStore();

  return (
    <WSElement className={styles.wrapper}>
      <WSQueries
        queries={{
          cardQuery
        }}
      >
        {({ cardQuery: { data: card } }) => {
          const isVirtualCard = [
            CardType.individualVirtualDebitCard,
            CardType.businessVirtualDebitCard
          ].includes(card.type);

          const isLockedCard = card.status === CardStatus.Frozen;

          const buttons =
            card.status === CardStatus.Inactive ? (
              <WSButtons forceFullWidth mt="XL">
                <WSButton.Secondary
                  icon="card"
                  onClick={() => {
                    otpProtector(card, () => {
                      openModal(ACTIVATE_CARD_MODAL, { cardId: card.cardId });
                    });
                  }}
                  data-testid="activateCard"
                >
                  Activate card
                </WSButton.Secondary>
              </WSButtons>
            ) : (
              <WSButtons forceFullWidth mt="XL">
                {isLockedCard ? null : card.pinSet ? (
                  <WSButton.Secondary
                    icon="calculator"
                    onClick={() => {
                      if (token && ids[card.cardId]) {
                        openModal(CHANGE_PIN_MODAL);
                      } else {
                        openModal(BANK_CARD_OTP_MODAL, {
                          cardId: card.cardId,
                          onSuccess: ({ token, cardId }) => {
                            setStore({
                              bankCardTokens: {
                                token,
                                ids: {
                                  ...ids,
                                  [card.cardId]: cardId
                                }
                              }
                            });
                            openModal(CHANGE_PIN_MODAL);
                          }
                        } as OTPModalInnerProps);
                      }
                    }}
                  >
                    Change PIN
                  </WSButton.Secondary>
                ) : (
                  <WSButton.Secondary
                    icon="calculator"
                    onClick={() => {
                      if (token && ids[card.cardId]) {
                        openModal(SET_PIN_MODAL);
                      } else {
                        openModal(BANK_CARD_OTP_MODAL, {
                          cardId: card.cardId,
                          onSuccess: ({ token, cardId }) => {
                            setStore({
                              bankCardTokens: {
                                token,
                                ids: {
                                  ...ids,
                                  [card.cardId]: cardId
                                }
                              }
                            });
                            openModal(SET_PIN_MODAL);
                          }
                        } as OTPModalInnerProps);
                      }
                    }}
                    data-testid="setCardPIN"
                  >
                    Set card PIN
                  </WSButton.Secondary>
                )}
                {isLockedCard ? (
                  <WSButton.Tertiary
                    icon="lock"
                    onClick={() => {
                      unlockCard(card.cardId);
                    }}
                    loading={unlockCardMeta.isLoading}
                  >
                    Unlock card
                  </WSButton.Tertiary>
                ) : (
                  <WSButton.Secondary
                    icon="lock"
                    onClick={() => {
                      lockCard(card.cardId);
                    }}
                    loading={lockCardMeta.isLoading}
                  >
                    Lock card
                  </WSButton.Secondary>
                )}

                {isLockedCard
                  ? null
                  : isVirtualCard ||
                    !featureFlagsQuery.data?.physicalCardReplace
                  ? null
                  : [
                      <WSButton.Secondary
                        key="replace"
                        icon="envelope"
                        onClick={() => {
                          openModal(REPLACE_CARD_CONFIRMATION_MODAL);
                        }}
                      >
                        Replace card
                      </WSButton.Secondary>,
                      <WSButton.Secondary
                        key="stolen"
                        icon="alert-circle"
                        onClick={() => {
                          openModal(LOST_OR_STOLEN_CONFIRMATION_MODAL);
                        }}
                      >
                        Report lost or stolen
                      </WSButton.Secondary>
                    ]}

                {isLockedCard ? null : (
                  <WSButton
                    destructive
                    icon="trash"
                    onClick={() => {
                      openModal(CONFIRM_TERMINATE_CARD_MODAL, {
                        async onSubmit() {
                          if (
                            isVirtualCard ||
                            !featureFlagsQuery.data?.physicalCardReplace
                          ) {
                            await terminateCard(cardId, {
                              onSuccess() {
                                history.push("/member/wallet/dashboard");
                              }
                            });
                          } else {
                            openModal(REPLACE_OR_TERMINATE_CARD_MODAL);
                          }
                        }
                      });
                    }}
                  >
                    Terminate card
                  </WSButton>
                )}
              </WSButtons>
            );

          return (
            <>
              <ChangePin cardId={card.cardId} />
              <SetPin cardId={card.cardId} />
              <ConfirmTerminateCard />
              <ReplaceCardConfirmation cardId={card.cardId} />
              <ReplaceOrTerminateCard cardId={card.cardId} />
              <LostOrStolenConfirmation cardId={card.cardId} />

              <WSElement className={styles.header}>
                <WSIcon
                  block
                  name="arrow-left"
                  className={styles.backIcon}
                  onClick={onBack}
                />
                <WSText weight="medium">Card details</WSText>
              </WSElement>
              <WSElement className={styles.main}>
                <Card mb="XL">
                  {isVirtualCard ? (
                    <WSText.Heading5 my="M">Virtual debit card</WSText.Heading5>
                  ) : (
                    <WSFlexBox.CenterY my="M" justify="space-between">
                      <WSText.Heading5>Physical debit card</WSText.Heading5>
                      <WSButton.Link
                        onClick={() =>
                          openInNewTab(
                            "https://www.allpointnetwork.com/locator.html"
                          )
                        }
                      >
                        Search for ATMs →
                      </WSButton.Link>
                    </WSFlexBox.CenterY>
                  )}

                  <WSFlexBox.Center>
                    {isVirtualCard ? (
                      <BankCard key={card.cardId} card={card} />
                    ) : (
                      <CardSuccessPhysical
                        isLocked={card.status === CardStatus.Frozen}
                        isLoading={unlockCardMeta.isLoading}
                        onClick={
                          card.status === CardStatus.Frozen
                            ? () => unlockCard(card.cardId)
                            : undefined
                        }
                        last4={card.last4Digits}
                      />
                    )}
                  </WSFlexBox.Center>
                </Card>
                <Card mb="XL">
                  <WSText.Heading5 mb="XL">Card details</WSText.Heading5>
                  <WSText weight="medium" mb="XS">
                    Billing address
                  </WSText>
                  <WSText mb="XS">
                    {card.address.addressLine1}
                    {card.address.addressLine2 &&
                      `, ${card.address.addressLine2}`}
                    <br />
                    {card.address.city}, {card.address.state},{" "}
                    {card.address.postalCode}
                  </WSText>
                  <WSText>{card.name}</WSText>
                  {buttons}
                  <WSErrorMessage
                    mt="XL"
                    contextKey="CardActions"
                    error={lockCardMeta.error || unlockCardMeta.error}
                  />
                </Card>
              </WSElement>
            </>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
